import { useEffect, useRef } from "react";
import { ActivityIndicator, Animated, Easing, Pressable, StyleSheet, Text, View } from "react-native";
import { PauseIcon, PlayIcon, SquareIcon, SpeakerHighIcon, WarningCircleIcon } from "@phosphor-icons/react";

import { colors, radii, spacing, typographyStyles, webTransitions } from "../../../../styles/design-system";
import { ChatTooltip } from "../../../common/tooltip/ChatTooltip";

export type ReadAloudStatus = "loading" | "playing" | "paused" | "error";

export interface ReadAloudControl {
  status: ReadAloudStatus;
  isActive: boolean;
  hasPlayed: boolean;
  elapsedSeconds: number;
  totalSeconds: number;
  onPlayPress: () => void;
  onPause: () => void;
  onResume: () => void;
  onStop: () => void;
}

export interface ReadAloudPlaybackControlProps {
  readAloudControl: ReadAloudControl;
}

const MIN_WAVE_BAR_HEIGHT = 3;
const waveHeights = [8, 14, 11, 18, 10, 15, 9, 17, 8, 14, 11, 18, 10, 15, 9, 8];

const formatTime = (seconds: number) => {
  const safeSeconds = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
  const minutes = Math.floor(safeSeconds / 60);
  const remainingSeconds = safeSeconds % 60;
  return `${minutes}:${String(remainingSeconds).padStart(2, "0")}`;
};

export const ReadAloudIndicator = ({
  elapsedSeconds,
  onPause,
  onResume,
  status,
  totalSeconds,
}: Pick<ReadAloudControl, "elapsedSeconds" | "onPause" | "onResume" | "status" | "totalSeconds">) => {
  const waveAnimationsRef = useRef(waveHeights.map(() => new Animated.Value(0)));
  const waveLoopsRef = useRef<Animated.CompositeAnimation[]>([]);
  const waveAnimations = waveAnimationsRef.current;

  useEffect(() => {
    waveLoopsRef.current.forEach((animation) => animation.stop());
    waveLoopsRef.current = [];

    if (status === "paused") {
      waveAnimations.forEach((animatedValue) => {
        animatedValue.stopAnimation();
      });
      return;
    }

    if (status !== "playing") {
      waveAnimations.forEach((animatedValue) => {
        animatedValue.stopAnimation(() => {
          animatedValue.setValue(0);
        });
      });
      return;
    }

    waveLoopsRef.current = waveAnimations.map((animatedValue, index) => {
      const riseDuration = 280 + (index % 5) * 60;
      const fallDuration = 420 + (index % 4) * 70;
      animatedValue.setValue(index % 2 === 0 ? 0.15 : 0.65);

      const animation = Animated.loop(
        Animated.sequence([
          Animated.timing(animatedValue, {
            toValue: 1,
            duration: riseDuration,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: false,
          }),
          Animated.timing(animatedValue, {
            toValue: 0,
            duration: fallDuration,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: false,
          }),
        ])
      );

      animation.start();
      return animation;
    });

    return () => {
      waveLoopsRef.current.forEach((animation) => animation.stop());
      waveLoopsRef.current = [];
    };
  }, [status, waveAnimations]);

  const isPaused = status === "paused";
  const isLoading = status === "loading";
  const isInactiveIndicator = isPaused || isLoading;

  return (
    <View style={styles.readAloudIndicator}>
      <Pressable
        disabled={isLoading}
        onPress={isPaused ? onResume : onPause}
        style={({ hovered, pressed }) => [
          styles.indicatorPlaybackButton,
          (hovered || pressed) && !isLoading && styles.indicatorPlaybackButtonActive,
        ]}
        aria-label={isLoading ? "Loading read aloud" : isPaused ? "Resume reading" : "Pause reading"}
        accessibilityLabel={isLoading ? "Loading read aloud" : isPaused ? "Resume reading" : "Pause reading"}
        accessibilityHint="Press to control read aloud playback">
        {isLoading ? (
          <ActivityIndicator
            size={12}
            color={colors.grey[500]}
            accessibilityLabel="Loading read aloud"
            aria-label="Loading read aloud"
          />
        ) : isPaused ? (
          <PlayIcon size={14} color={colors.grey[500]} weight="fill" />
        ) : (
          <PauseIcon size={14} color={colors.grey[500]} weight="fill" />
        )}
      </Pressable>
      <View style={styles.waveformContainer}>
        {waveHeights.map((height, index) => (
          <Animated.View
            key={index}
            style={[
              styles.waveBar,
              {
                height: waveAnimations[index].interpolate({
                  inputRange: [0, 1],
                  outputRange: [MIN_WAVE_BAR_HEIGHT, height],
                }),
              },
              isInactiveIndicator && styles.waveBarPaused,
            ]}
          />
        ))}
      </View>
      <Text style={styles.indicatorTime}>
        {formatTime(elapsedSeconds)} / {formatTime(totalSeconds)}
      </Text>
    </View>
  );
};

export const ReadAloudPlaybackControl = ({ readAloudControl }: ReadAloudPlaybackControlProps) => {
  const { elapsedSeconds, isActive, onPause, onPlayPress, onResume, onStop, status, totalSeconds } = readAloudControl;
  const isLoading = status === "loading";
  const isStopState = isActive && (status === "playing" || status === "paused");
  const showError = status === "error" && !isActive;
  const showIndicator = isActive;

  const buttonIcon = isLoading ? (
    <ActivityIndicator
      size={14}
      color={colors.brand[600]}
      accessibilityLabel="Loading read aloud"
      aria-label="Loading read aloud"
    />
  ) : showError ? (
    <WarningCircleIcon size={17} color={colors.error[600]} weight="fill" />
  ) : isStopState ? (
    <SquareIcon size={16} color={colors.brand[600]} weight="fill" />
  ) : (
    <SpeakerHighIcon size={17} color={colors.grey[500]} weight="regular" />
  );

  const actionButtonLabel = isStopState
    ? "Stop reading"
    : isLoading
      ? "Loading read aloud"
      : showError
        ? "Retry read aloud"
        : "Read aloud";
  const tooltipLabel = isLoading ? null : isStopState ? "Stop reading" : showError ? "Retry read aloud" : "Read aloud";

  const handleReadAloudActionPress = () => {
    if (isLoading) {
      return;
    }

    if (isStopState) {
      onStop();
      return;
    }

    onPlayPress();
  };

  return (
    <View style={styles.readAloudContainer}>
      {tooltipLabel ? (
        <ChatTooltip content={tooltipLabel}>
          <Pressable
            onPress={handleReadAloudActionPress}
            disabled={isLoading}
            style={({ hovered, pressed }) => [
              styles.readAloudButtonBase,
              isStopState ? styles.readAloudButtonStop : styles.readAloudButtonIdle,
              showError && styles.readAloudButtonError,
              (hovered || pressed) && !isLoading && styles.readAloudButtonActive,
              isLoading && styles.readAloudButtonDisabled,
            ]}
            aria-label={actionButtonLabel}
            accessibilityLabel={actionButtonLabel}
            accessibilityHint="Press to control read aloud playback"
            accessibilityState={{ disabled: isLoading }}>
            {buttonIcon}
          </Pressable>
        </ChatTooltip>
      ) : (
        <Pressable
          onPress={handleReadAloudActionPress}
          disabled={isLoading}
          style={({ hovered, pressed }) => [
            styles.readAloudButtonBase,
            isStopState ? styles.readAloudButtonStop : styles.readAloudButtonIdle,
            showError && styles.readAloudButtonError,
            (hovered || pressed) && !isLoading && styles.readAloudButtonActive,
            isLoading && styles.readAloudButtonDisabled,
          ]}
          aria-label={actionButtonLabel}
          accessibilityLabel={actionButtonLabel}
          accessibilityHint="Press to control read aloud playback"
          accessibilityState={{ disabled: isLoading }}>
          {buttonIcon}
        </Pressable>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  readAloudContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  readAloudButtonBase: {
    padding: spacing.xs,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: "transparent",
    ...webTransitions.fast,
  },
  readAloudButtonIdle: {
    borderRadius: radii.md,
    backgroundColor: "transparent",
  },
  readAloudButtonStop: {
    borderRadius: radii.md,
    backgroundColor: colors.brand[100],
  },
  readAloudButtonError: {
    borderRadius: radii.md,
    borderColor: colors.error[200],
    backgroundColor: colors.error[50],
  },
  readAloudButtonActive: {
    backgroundColor: colors.grey[200],
    borderColor: "transparent",
  },
  readAloudButtonDisabled: {
    opacity: 0.7,
  },
  readAloudIndicator: {
    marginBottom: spacing.xxs,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xxs,
    borderRadius: 12,
    backgroundColor: "rgba(242, 244, 247, 0.6)",
    borderWidth: 1,
    borderColor: colors.grey[100],
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
    minHeight: 44,
  },
  indicatorPlaybackButton: {
    width: 24,
    height: 24,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
  },
  indicatorPlaybackButtonActive: {
    backgroundColor: colors.grey[200],
  },
  waveformContainer: {
    flex: 1,
    height: 18,
    flexDirection: "row",
    alignItems: "flex-end",
    gap: 2,
  },
  waveBar: {
    flex: 1,
    minWidth: 2,
    borderRadius: 6,
    backgroundColor: "rgba(154, 164, 255, 0.95)",
  },
  waveBarPaused: {
    backgroundColor: "rgba(154, 164, 255, 0.5)",
  },
  indicatorTime: {
    color: colors.grey[400],
    ...typographyStyles.textXs,
    fontVariant: ["tabular-nums"],
    minWidth: 78,
    textAlign: "right",
  },
});
